import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../helpers/axios'

export interface Topic {
    id: number
    name: string
    description: string | null
    keywords: string | null
}

export interface TopicInput {
    name: string
    description: string | null
    keywords: string | null
}

const TOPICS_QUERY_KEY = 'topics'
const TOPICS_PATH = '/chat/topics/'

export const useTopics = () => {
    const { data: topics, isLoading, error } = useQuery<Topic[]>({
        queryKey: [TOPICS_QUERY_KEY],
        queryFn: async () => {
            const res = await axiosInstance.get<Topic[]>(TOPICS_PATH)
            return res.data
        },
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        topics: topics ?? [],
        isLoading,
        error: error ?? null,
    }
}

export const useCreateTopic = () => {
    const queryClient = useQueryClient()

    return useMutation<Topic, unknown, TopicInput>({
        mutationFn: async (input) => {
            const res = await axiosInstance.post<Topic>(TOPICS_PATH, input)
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries([TOPICS_QUERY_KEY])
        },
    })
}

export const useUpdateTopic = () => {
    const queryClient = useQueryClient()

    return useMutation<Topic, unknown, { id: number } & TopicInput>({
        mutationFn: async ({ id, ...input }) => {
            const res = await axiosInstance.put<Topic>(
                `${TOPICS_PATH}${id}/`,
                input,
            )
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries([TOPICS_QUERY_KEY])
        },
    })
}

export const useDeleteTopic = () => {
    const queryClient = useQueryClient()

    return useMutation<void, unknown, number>({
        mutationFn: async (id) => {
            await axiosInstance.delete(`${TOPICS_PATH}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries([TOPICS_QUERY_KEY])
        },
    })
}
